import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Truck, FileText, ChevronLeft, Plus, CheckCircle2, XCircle, ClipboardCheck, AlertTriangle, PackageCheck, RefreshCw, X, Trash2, ShieldAlert } from "lucide-react";
import { RejectModal } from "./POsTab";
import ExportXlsxButton from "./ExportXlsxButton";
import { useOutlet } from "@/lib/outlet";

const STATUS_STYLES = {
  pending_approval: "bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)]",
  approved: "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]",
  rejected: "bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]",
};

const fmtTs = (v) => {
  if (!v) return "";
  try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(v); }
};

// Legacy GRNs (before workflow) have no status field — treat them as approved.
const effectiveStatus = (g) => g.status || "approved";

export default function GRNsTab({ presetFilter } = {}) {
  const { user } = useAuth();
  // Approver = full admin OR the limited `approver` role (Ranjith). All
  // approve/reject/master-override buttons and bulk approve check this flag.
  const isAdmin = user?.role === "admin" || user?.role === "approver";
  const { outletParam } = useOutlet();
  const [grns, setGrns] = useState([]);
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ mode: "list" });
  const [filter, setFilter] = useState("all");
  const [rejectFor, setRejectFor] = useState(null); // {id, grn_number}
  const [directOpen, setDirectOpen] = useState(false);
  const [masterFor, setMasterFor] = useState(null); // GRN object or null
  const [selected, setSelected] = useState({});
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);

  useEffect(() => {
    if (presetFilter) setFilter(presetFilter);
  }, [presetFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const [grnsRes, posRes, vendorsRes] = await Promise.all([
        api.get("/staff/grns"),
        api.get("/staff/purchase-orders"),
        api.get("/staff/vendors"),
      ]);
      setGrns(grnsRes.data);
      setPos(posRes.data);
      setVendors(vendorsRes.data || []);
    } catch { toast.error("Could not load GRNs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Approved POs are receivable too — the vendor is delivering; we shouldn't wait on an
  // extra "send" click before receipt. Sent/partially_received are still the classic path.
  const receivablePOs = pos.filter((p) => ["approved", "sent", "partially_received"].includes(p.status));
  // v2 — outlet filter (soft): untagged rows match every outlet.
  const outletFiltered = useMemo(() => {
    if (!outletParam) return grns;
    return grns.filter((g) => !g.outlet || g.outlet === outletParam);
  }, [grns, outletParam]);
  const pendingCount = useMemo(() => outletFiltered.filter((g) => effectiveStatus(g) === "pending_approval").length, [outletFiltered]);
  const filtered = filter === "all" ? outletFiltered : outletFiltered.filter((g) => effectiveStatus(g) === filter);

  const downloadPdf = async (grn) => {
    try {
      const res = await api.get(`/staff/grns/${grn.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBB-${(grn.grn_number || "GRN").replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const approve = async (g) => {
    // CTRL-03 — invoice_number is mandatory to approve.
    const existingInv = (g.invoice_number || "").trim();
    let invoiceNo = existingInv;
    if (!invoiceNo) {
      invoiceNo = (window.prompt(
        `Vendor invoice number for GRN ${g.grn_number}?\n(Required to approve — CTRL-03)`
      ) || "").trim();
      if (!invoiceNo) { toast.error("Invoice number is required"); return; }
    }
    if (!window.confirm(`Approve GRN ${g.grn_number} against invoice ${invoiceNo}?\nThis will update PO receipts and inventory stock.`)) return;

    const attempt = async (justification = "") => {
      const body = { invoice_number: invoiceNo };
      if (justification) body.override_justification = justification;
      return api.post(`/staff/grns/${g.id}/approve`, body);
    };

    try {
      await attempt();
      toast.success("GRN approved — stock & PO updated");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Approval failed";
      // CTRL-04 self-approval — offer a manager override prompt if the message matches.
      if (/CTRL-04.*Self-approval|override_justification/i.test(detail)) {
        const justification = (window.prompt(
          `${detail}\n\nEnter manager/director justification to override:`
        ) || "").trim();
        if (!justification) { toast.error("Approval cancelled — no justification provided"); return; }
        try {
          await attempt(justification);
          toast.success("GRN approved with override — audit logged");
          load();
        } catch (err2) {
          toast.error(err2?.response?.data?.detail || "Override rejected");
        }
      } else {
        toast.error(detail);
      }
    }
  };

  const submitReject = async (reason) => {
    if (!rejectFor) return;
    try {
      await api.post(`/staff/grns/${rejectFor.id}/reject`, { reason });
      toast.success("GRN rejected");
      setRejectFor(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Rejection failed"); }
  };

  const submitMasterApprove = async ({ reason, invoiceNo }) => {
    if (!masterFor) return;
    try {
      await api.post(`/staff/grns/${masterFor.id}/master-approve`, {
        reason, invoice_number: invoiceNo || "",
      });
      toast.success(`Master-override approved · logged`);
      setMasterFor(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Master approval failed"); }
  };

  // Bulk approve GRNs — admin only. Opens a small modal to collect the vendor
  // invoice number per selected GRN (CTRL-03 requires one), then approves
  // sequentially. Any single-GRN failure is surfaced without halting the batch.
  const [bulkModal, setBulkModal] = useState(null); // {rows: [{grn, invoiceNo, justification}]}
  const openBulkModal = () => {
    const targets = filtered.filter((g) => selected[g.id] && effectiveStatus(g) === "pending_approval");
    if (!targets.length) { toast.error("Select at least one pending GRN"); return; }
    setBulkModal({
      rows: targets.map((g) => ({
        grn: g,
        invoiceNo: (g.invoice_number || "").trim(),
        justification: "",
      })),
    });
  };
  const submitBulk = async () => {
    if (!bulkModal) return;
    const missingInv = bulkModal.rows.filter((r) => !r.invoiceNo.trim());
    if (missingInv.length) {
      toast.error(`Enter invoice # for ${missingInv.length} GRN${missingInv.length === 1 ? "" : "s"} (required to approve)`);
      return;
    }
    let ok = 0, fail = 0;
    const errors = [];
    for (const r of bulkModal.rows) {
      const body = { invoice_number: r.invoiceNo.trim() };
      if (r.justification.trim()) body.override_justification = r.justification.trim();
      try {
        await api.post(`/staff/grns/${r.grn.id}/approve`, body);
        ok++;
      } catch (err) {
        const detail = err?.response?.data?.detail || "failed";
        // Auto-retry once with justification if backend flagged CTRL-04 and one was already supplied
        // otherwise surface the error to be filled and re-submitted.
        fail++;
        errors.push(`${r.grn.grn_number}: ${detail}`);
      }
    }
    if (ok) toast.success(`${ok} GRN${ok === 1 ? "" : "s"} approved`);
    if (fail) toast.error(`${fail} failed — ${errors[0]}${errors.length > 1 ? ` (+${errors.length - 1} more)` : ""}`);
    setBulkModal(null);
    setSelected({});
    load();
  };

  if (view.mode === "pick-po") {
    return (
      <div data-testid="grn-pick-po">
        <button onClick={() => setView({ mode: "list" })} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--ink-soft))] mb-4">
          <ChevronLeft size={14} /> Back
        </button>
        <h2 className="text-2xl">Choose a PO to receive against</h2>
        <p className="text-sm text-[hsl(var(--ink-soft))] mt-1">Approved, Sent, and Partially-received POs are shown. GRN will be posted for admin approval.</p>
        <div className="mt-6 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
          {receivablePOs.length === 0 && (
            <div className="p-10 text-center text-[hsl(var(--ink-soft))]">No open POs — create and approve a PO first.</div>
          )}
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {receivablePOs.map((po) => {
              const total = (po.lines || []).length;
              const pending = (po.lines || []).filter((l) => Number(l.qty_ordered) - Number(l.qty_received || 0) > 1e-6).length;
              const isPartial = po.status === "partially_received";
              return (
              <li key={po.id}>
                <button data-testid={`grn-pick-po-${po.id}`} onClick={() => setView({ mode: "create", po })} className="w-full text-left p-4 hover:bg-[hsl(var(--bg))] flex items-center justify-between gap-4">
                  <div>
                    <div className="font-mono text-sm text-[hsl(var(--brass-2))]">{po.po_number}</div>
                    <div className="text-sm font-semibold mt-0.5">{po.vendor_snapshot?.name}</div>
                    <div className="text-xs text-[hsl(var(--ink-soft))]">
                      {total} line{total === 1 ? "" : "s"}
                      {isPartial && pending !== total && (
                        <> · <span className="text-[hsl(var(--forest))] font-semibold">{pending} pending</span></>
                      )}
                      {" "}· ₹{Number(po.total).toLocaleString("en-IN")} · {po.status.replace("_", " ")}
                    </div>
                  </div>
                  <div className="text-xs text-[hsl(var(--ink-soft))]">Expected: {po.expected_delivery_date || "—"}</div>
                </button>
              </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }

  if (view.mode === "create") {
    return <GRNForm po={view.po} onDone={() => { setView({ mode: "list" }); load(); }} onCancel={() => setView({ mode: "pick-po" })} />;
  }

  return (
    <div data-testid="grns-tab">
      {isAdmin && pendingCount > 0 && filter !== "pending_approval" && (
        <div className="mb-4 flex items-center gap-3 p-3 border border-[hsl(var(--brass))] bg-[hsl(var(--brass)/0.1)] text-sm">
          <ClipboardCheck size={16} className="text-[hsl(var(--brass-2))]" />
          <span><b>{pendingCount}</b> GRN{pendingCount === 1 ? "" : "s"} awaiting your approval — stock updates pending.</span>
          <button data-testid="grn-goto-pending" onClick={() => setFilter("pending_approval")} className="ml-auto text-xs uppercase tracking-wider text-[hsl(var(--brass-2))] hover:underline">Review →</button>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex flex-wrap gap-1 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] rounded-full p-1">
          {["all", "pending_approval", "approved", "rejected"].map((f) => (
            <button
              key={f}
              data-testid={`grn-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-[11px] uppercase tracking-wider rounded-full ${filter === f ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
            >
              {f.replace(/_/g, " ")}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs text-[hsl(var(--ink-soft))]">Stock updates on admin approval.</div>
          {isAdmin && selectedIds.length > 0 && (
            <button
              data-testid="grn-bulk-approve"
              onClick={openBulkModal}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]"
            >
              <CheckCircle2 size={13} /> Approve {selectedIds.length} selected
            </button>
          )}
          <ExportXlsxButton
            url="/staff/exports/grns.xlsx"
            params={{
              ...(outletParam ? { outlet: outletParam } : {}),
              ...(filter !== "all" ? { status: filter } : {}),
            }}
            testId="grn-export-xlsx"
            label="Export .xlsx"
          />
          <button data-testid="grn-new-direct" onClick={() => setDirectOpen(true)} className="inline-flex items-center gap-1 text-sm px-3 py-2 border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] rounded hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors">
            <Plus size={14} /> Walk-in GRN
          </button>
          <button data-testid="grn-new" onClick={() => setView({ mode: "pick-po" })} className="btn-brass !py-2 !px-4 text-sm">
            <Plus size={14} /> New GRN
          </button>
        </div>
      </div>

      <div className="mt-6 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
        {loading && <div className="p-6 text-[hsl(var(--ink-soft))]">Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div data-testid="grns-empty" className="p-10 text-center text-[hsl(var(--ink-soft))]">
            <Truck className="mx-auto mb-2 opacity-40" size={28} />
            No goods receipts in this view.
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <>
            {isAdmin && filter === "pending_approval" && filtered.length > 0 && (
              <div className="px-4 py-2 border-b border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] text-xs text-[hsl(var(--ink-soft))] flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid="grn-select-all"
                  className="h-4 w-4 accent-[hsl(var(--forest))]"
                  checked={selectedIds.length === filtered.length && filtered.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) setSelected(Object.fromEntries(filtered.map((g) => [g.id, true])));
                    else setSelected({});
                  }}
                />
                <span>Select all pending ({filtered.length})</span>
              </div>
            )}
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {filtered.map((g) => {
              const status = effectiveStatus(g);
              return (
                <li key={g.id} data-testid={`grn-${g.id}`} className="p-4 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-start">
                  <div className="pt-1">
                    {isAdmin && status === "pending_approval" ? (
                      <input
                        type="checkbox"
                        data-testid={`grn-select-${g.id}`}
                        checked={!!selected[g.id]}
                        onChange={(e) => setSelected({ ...selected, [g.id]: e.target.checked })}
                        className="h-4 w-4 accent-[hsl(var(--forest))]"
                        aria-label={`Select ${g.grn_number}`}
                      />
                    ) : <div className="w-4" />}
                  </div>
                  <div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div className="font-mono text-sm text-[hsl(var(--brass-2))]">{g.grn_number}</div>
                      <span data-testid={`grn-status-${g.id}`} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLES[status] || ""}`}>{status.replace(/_/g, " ")}</span>
                      {g.is_direct ? (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[hsl(var(--brass)/0.15)] text-[hsl(var(--brass-2))]">Walk-in</span>
                      ) : (
                        <div className="text-xs text-[hsl(var(--ink-soft))] font-mono">against {g.po_number}</div>
                      )}
                      <div className="text-sm font-semibold">{g.vendor_snapshot?.name}</div>
                      <div className="text-xs">₹{Number(g.total).toLocaleString("en-IN")}</div>
                    </div>
                    <div className="mt-1 text-xs text-[hsl(var(--ink-soft))]">
                      Received on {g.received_date} · by {g.received_by} · {(g.lines || []).length} lines
                      {g.approved_at && <> · Approved {fmtTs(g.approved_at)}{g.approved_by ? ` by ${g.approved_by}` : ""}</>}
                      {g.rejected_at && <> · Rejected {fmtTs(g.rejected_at)}{g.rejected_by ? ` by ${g.rejected_by}` : ""}</>}
                    </div>
                    {g.rejection_reason && (
                      <div data-testid={`grn-reject-reason-${g.id}`} className="mt-2 inline-flex items-start gap-2 text-xs px-2.5 py-1.5 rounded bg-[hsl(6_60%_95%)] text-[hsl(6_62%_38%)] border border-[hsl(6_62%_45%_/_0.3)]">
                        <AlertTriangle size={12} className="mt-0.5" />
                        <span><b>Rejected:</b> {g.rejection_reason}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {status === "pending_approval" && isAdmin && (
                      <>
                        <button data-testid={`grn-approve-${g.id}`} onClick={() => approve(g)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]">
                          <CheckCircle2 size={12} /> Approve
                        </button>
                        <button data-testid={`grn-reject-${g.id}`} onClick={() => setRejectFor({ id: g.id, grn_number: g.grn_number })} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
                          <XCircle size={12} /> Reject
                        </button>
                        <button data-testid={`grn-master-approve-${g.id}`} onClick={() => setMasterFor(g)} title="Admin master override — bypasses all CTRL checks. Logged." className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)] hover:bg-[hsl(6_62%_45%)] hover:text-white transition-colors">
                          <ShieldAlert size={12} /> Master approve
                        </button>
                      </>
                    )}
                    <button data-testid={`grn-pdf-${g.id}`} onClick={() => downloadPdf(g)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))]">
                      <FileText size={12} /> PDF
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          </>
        )}
      </div>

      {rejectFor && (
        <RejectModal target={`GRN ${rejectFor.grn_number}`} onCancel={() => setRejectFor(null)} onSubmit={submitReject} testidPrefix="grn-reject" />
      )}
      {directOpen && (
        <DirectGRNModal
          vendors={vendors}
          onClose={() => setDirectOpen(false)}
          onSaved={() => { setDirectOpen(false); load(); }}
        />
      )}
      {bulkModal && (
        <BulkApproveModal
          rows={bulkModal.rows}
          onChange={(idx, patch) => setBulkModal({ rows: bulkModal.rows.map((r, i) => i === idx ? { ...r, ...patch } : r) })}
          onCancel={() => setBulkModal(null)}
          onSubmit={submitBulk}
        />
      )}
      {masterFor && (
        <MasterApproveModal
          grn={masterFor}
          onCancel={() => setMasterFor(null)}
          onSubmit={submitMasterApprove}
        />
      )}
    </div>
  );
}

function MasterApproveModal({ grn, onCancel, onSubmit }) {
  const [reason, setReason] = useState("");
  const [invoiceNo, setInvoiceNo] = useState((grn.invoice_number || "").trim());
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 5) { toast.error("Reason must be at least 5 characters"); return; }
    setBusy(true);
    try { await onSubmit({ reason: reason.trim(), invoiceNo: invoiceNo.trim() }); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="grn-master-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bg-[hsl(var(--bg))] border border-[hsl(6_62%_45%)] w-full max-w-lg p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <ShieldAlert size={20} className="text-[hsl(6_62%_45%)] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="eyebrow text-[hsl(6_62%_45%)]">Admin master override</div>
            <h2 className="mt-1 display text-xl">Force-approve {grn.grn_number}</h2>
            <p className="mt-2 text-xs text-[hsl(var(--ink-soft))] leading-relaxed">
              This bypasses <b>every</b> CTRL rule — zero-rate lines, receipt tolerance, invoice
              requirement, segregation of duties. The action is <b>permanently logged</b> in the audit trail with
              the reason below. Use only when you've decided the underlying business fact is correct
              and the rule is a false-positive.
            </p>
          </div>
          <button onClick={onCancel} className="text-[hsl(var(--ink-soft))]"><X size={16} /></button>
        </div>

        <div className="mt-5 space-y-3">
          <label className="block">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">
              Reason for override * (min 5 chars)
            </span>
            <textarea
              data-testid="grn-master-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Vendor sent short and confirmed on WhatsApp — accepting at PO rate. Rate mismatch is intentional."
              className="tbb-inp w-full resize-none text-sm"
              autoFocus
            />
          </label>
          <label className="block">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">
              Invoice # (optional — defaults to "MASTER-OVERRIDE")
            </span>
            <input
              data-testid="grn-master-invoice"
              value={invoiceNo}
              onChange={(e) => setInvoiceNo(e.target.value)}
              placeholder="Vendor invoice number if available"
              className="tbb-inp w-full text-sm"
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onCancel} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
          <button
            data-testid="grn-master-submit"
            onClick={submit}
            disabled={busy || reason.trim().length < 5}
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded bg-[hsl(6_62%_45%)] text-white disabled:opacity-50"
          >
            <ShieldAlert size={13} /> {busy ? "Approving…" : "Force approve"}
          </button>
        </div>
        <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
      </div>
    </div>
  );
}

function BulkApproveModal({ rows, onChange, onCancel, onSubmit }) {
  const [busy, setBusy] = useState(false);
  const total = rows.reduce((sum, r) => sum + Number(r.grn.total || 0), 0);
  const anySelfApprove = rows.some((r) => r.grn.received_by && r.grn.po_id && Number(r.grn.total) > 2000);
  const submit = async () => { setBusy(true); try { await onSubmit(); } finally { setBusy(false); } };
  return (
    <div data-testid="grn-bulk-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-3xl p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="eyebrow">Bulk approve GRNs</div>
            <h2 className="mt-1 display text-2xl">
              Approve {rows.length} · ₹{total.toLocaleString("en-IN")}
            </h2>
            <p className="mt-1 text-xs text-[hsl(var(--ink-soft))]">
              Invoice number is required per row (CTRL-03). Add a manager override justification only if the same user raised the PO (CTRL-04).
            </p>
          </div>
          <button onClick={onCancel} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
        </div>

        <div className="mt-5 border border-[hsl(var(--rule))]">
          <table className="w-full text-xs">
            <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] uppercase tracking-wider">
              <tr>
                <th className="text-left p-2">GRN</th>
                <th className="text-left p-2">Vendor</th>
                <th className="text-right p-2">Total</th>
                <th className="text-left p-2">Invoice # *</th>
                {anySelfApprove && <th className="text-left p-2">Justification (if CTRL-04)</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--rule))]">
              {rows.map((r, i) => (
                <tr key={r.grn.id} data-testid={`grn-bulk-row-${r.grn.id}`}>
                  <td className="p-2 font-mono text-[hsl(var(--brass-2))]">{r.grn.grn_number}</td>
                  <td className="p-2">{r.grn.vendor_snapshot?.name || "—"}</td>
                  <td className="p-2 text-right">₹{Number(r.grn.total).toLocaleString("en-IN")}</td>
                  <td className="p-2">
                    <input
                      data-testid={`grn-bulk-invoice-${r.grn.id}`}
                      value={r.invoiceNo}
                      onChange={(e) => onChange(i, { invoiceNo: e.target.value })}
                      placeholder="e.g. INV-2026-0421"
                      className="tbb-inp w-full text-xs !py-1"
                    />
                  </td>
                  {anySelfApprove && (
                    <td className="p-2">
                      <input
                        data-testid={`grn-bulk-just-${r.grn.id}`}
                        value={r.justification}
                        onChange={(e) => onChange(i, { justification: e.target.value })}
                        placeholder="only if needed"
                        className="tbb-inp w-full text-xs !py-1"
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onCancel} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
          <button data-testid="grn-bulk-submit" onClick={submit} disabled={busy}
                  className="btn-brass !py-2.5 !px-5 text-sm">
            {busy ? "Approving…" : `Approve ${rows.length}`}
          </button>
        </div>
        <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
      </div>
    </div>
  );
}

function DirectGRNModal({ vendors, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const activeVendors = (vendors || []).filter((v) => v.active !== false);
  const [vendorId, setVendorId] = useState(activeVendors[0]?.id || "");
  const [receivedDate, setReceivedDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [lines, setLines] = useState([
    { item_name: "", unit: "kg", qty_received: 1, rate: 0, remarks: "" },
  ]);

  const total = lines.reduce((s, l) => s + Number(l.qty_received || 0) * Number(l.rate || 0), 0);

  const update = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { item_name: "", unit: "kg", qty_received: 1, rate: 0, remarks: "" }]);
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    if (!vendorId) { toast.error("Pick a vendor"); return; }
    const valid = lines
      .map((l) => ({ ...l, item_name: (l.item_name || "").trim(), qty_received: Number(l.qty_received) || 0, rate: Number(l.rate) || 0 }))
      .filter((l) => l.item_name && l.qty_received > 0);
    if (valid.length === 0) { toast.error("Add at least one line with qty > 0"); return; }
    setSaving(true);
    try {
      await api.post("/staff/grns/direct", {
        vendor_id: vendorId,
        received_date: receivedDate,
        lines: valid,
        notes,
      });
      toast.success("Walk-in GRN created — awaits admin approval");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="direct-grn-modal" className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={save} className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-3xl my-6 shadow-xl">
        <div className="p-4 border-b border-[hsl(var(--rule))] flex items-center justify-between">
          <div>
            <div className="eyebrow">Walk-in GRN</div>
            <h2 className="mt-1 text-xl display">Record a receipt without a PO</h2>
            <div className="text-xs text-[hsl(var(--ink-soft))] mt-0.5">For spot buys / market runs. Still requires admin approval before stock &amp; ledger update.</div>
          </div>
          <button type="button" onClick={onClose} data-testid="direct-grn-close" className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Vendor *
              <select required data-testid="direct-grn-vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)} className="menu-inp mt-1 w-full">
                <option value="">— Select vendor —</option>
                {activeVendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </label>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Received date *
              <input required type="date" data-testid="direct-grn-date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} className="menu-inp mt-1 w-full" />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wide text-[hsl(var(--ink-soft))]">Items received</div>
              <button type="button" data-testid="direct-grn-add-line" onClick={addLine} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--forest))] hover:underline">
                <Plus size={12} /> Add line
              </button>
            </div>
            <div className="border border-[hsl(var(--rule))] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--bg-2))] text-left text-[11px] uppercase tracking-wide text-[hsl(var(--ink-soft))]">
                  <tr>
                    <th className="px-2 py-1.5">Item</th>
                    <th className="px-2 py-1.5 w-16 text-right">Qty</th>
                    <th className="px-2 py-1.5 w-16">Unit</th>
                    <th className="px-2 py-1.5 w-24 text-right">Rate ₹</th>
                    <th className="px-2 py-1.5 w-24 text-right">Amount ₹</th>
                    <th className="px-2 py-1.5">Remark</th>
                    <th className="px-2 py-1.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i} data-testid={`direct-grn-line-${i}`} className="border-t border-[hsl(var(--rule))]">
                      <td className="px-2 py-1.5">
                        <input data-testid={`direct-grn-name-${i}`} required value={l.item_name} onChange={(e) => update(i, { item_name: e.target.value })} className="menu-inp !py-1 !px-1.5 text-xs w-full" placeholder="e.g. Coriander leaves" />
                      </td>
                      <td className="px-2 py-1.5 text-right"><input data-testid={`direct-grn-qty-${i}`} type="number" min="0" step="0.01" value={l.qty_received} onChange={(e) => update(i, { qty_received: e.target.value })} className="menu-inp !py-1 !px-1.5 text-xs text-right w-full" /></td>
                      <td className="px-2 py-1.5"><input data-testid={`direct-grn-unit-${i}`} value={l.unit} onChange={(e) => update(i, { unit: e.target.value })} className="menu-inp !py-1 !px-1.5 text-xs w-full" /></td>
                      <td className="px-2 py-1.5 text-right"><input data-testid={`direct-grn-rate-${i}`} type="number" min="0" step="0.01" value={l.rate} onChange={(e) => update(i, { rate: e.target.value })} className="menu-inp !py-1 !px-1.5 text-xs text-right w-full" /></td>
                      <td className="px-2 py-1.5 text-right text-xs font-semibold">₹{(Number(l.qty_received || 0) * Number(l.rate || 0)).toFixed(2)}</td>
                      <td className="px-2 py-1.5"><input value={l.remarks} onChange={(e) => update(i, { remarks: e.target.value })} className="menu-inp !py-1 !px-1.5 text-xs w-full" placeholder="optional" /></td>
                      <td className="px-2 py-1.5">
                        {lines.length > 1 && (
                          <button type="button" data-testid={`direct-grn-remove-${i}`} onClick={() => removeLine(i)} className="text-[hsl(6_62%_45%)]"><Trash2 size={12} /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            Notes (optional)
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className="menu-inp mt-1 w-full" placeholder="Bill no., driver, etc." />
          </label>

          <div className="flex items-center justify-between gap-3">
            <div className="text-lg" data-testid="direct-grn-total">Total: <span className="font-semibold">₹{total.toFixed(2)}</span></div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
              <button type="submit" data-testid="direct-grn-save" disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">{saving ? "Saving…" : "Save (pending approval)"}</button>
            </div>
          </div>
        </div>
        <style>{`.menu-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.menu-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
      </form>
    </div>
  );
}

function GRNForm({ po, onDone, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [receivedDate, setReceivedDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // Dynamic thresholds from procurement Settings (falls back to spec defaults).
  const [devAmber, setDevAmber] = useState(5);
  const [devRed, setDevRed] = useState(15);
  useEffect(() => {
    api.get("/staff/settings").then(({ data }) => {
      const a = Number(data?.grn_rate_deviation_amber_pct);
      const r = Number(data?.grn_rate_deviation_red_pct);
      if (Number.isFinite(a) && a > 0) setDevAmber(a);
      if (Number.isFinite(r) && r > 0) setDevRed(r);
    }).catch(() => {});
  }, []);
  const [lines, setLines] = useState(() =>
    (po.lines || [])
      .map((l) => {
        const pending = Number(l.qty_ordered) - Number(l.qty_received || 0);
        return {
          item_id: l.item_id || "",
          item_name: l.item_name,
          unit: l.unit,
          qty_ordered: Number(l.qty_ordered),
          qty_pending: pending,
          qty_received: pending,   // default to full pending
          rate: Number(l.rate),
          po_rate: Number(l.rate), // frozen for CTRL-05 deviation compare
          remarks: "",
        };
      })
      // Skip lines that are already fully received — they add zero-value noise to the GRN.
      .filter((l) => l.qty_pending > 1e-6),
  );

  const updateLine = (i, patch) => setLines((p) => p.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const receiveAll = () => setLines((p) => p.map((l) => ({ ...l, qty_received: l.qty_pending })));
  const clearAll = () => setLines((p) => p.map((l) => ({ ...l, qty_received: 0 })));
  const receivedLineCount = lines.filter((l) => Number(l.qty_received || 0) > 0).length;

  const totals = useMemo(() => {
    const st = lines.reduce((s, l) => s + Number(l.qty_received || 0) * Number(l.rate || 0), 0);
    const tp = Number(po.tax_percent) || 0;
    const ta = st * tp / 100;
    return { subtotal: st, tax_amount: ta, total: st + ta, tax_percent: tp };
  }, [lines, po.tax_percent]);

  const save = async () => {
    if (lines.length === 0) {
      toast.error("Nothing to receive on this PO — all lines are already fully received.");
      return;
    }
    for (const [i, l] of lines.entries()) {
      const qr = Number(l.qty_received || 0);
      if (qr < 0) { toast.error(`Line ${i + 1}: received qty cannot be negative`); return; }
      // Over-receipt is allowed (vendor sent extra) BUT the receiver must add a remark so the audit is clear.
      if (qr > l.qty_pending + 1e-6 && !(l.remarks || "").trim()) {
        toast.error(`Line ${i + 1}: received (${qr}) is more than pending (${l.qty_pending}). Add a remark explaining the over-receipt.`);
        return;
      }
    }
    const hasAny = lines.some((l) => Number(l.qty_received || 0) > 0);
    if (!hasAny) { toast.error("Enter received qty for at least one line"); return; }
    setSaving(true);
    try {
      const payload = {
        po_id: po.id,
        received_date: receivedDate,
        notes,
        lines: lines.map((l) => ({
          item_id: l.item_id, item_name: l.item_name, unit: l.unit,
          qty_ordered: l.qty_ordered, qty_received: Number(l.qty_received || 0),
          rate: Number(l.rate || 0), remarks: l.remarks || "",
        })),
      };
      const { data } = await api.post("/staff/grns", payload);
      toast.success(`GRN ${data.grn_number} submitted for admin approval`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "GRN post failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="grn-form">
      <button onClick={onCancel} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--ink-soft))] mb-4">
        <ChevronLeft size={14} /> Back
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl">New GRN</h2>
          <div className="text-sm text-[hsl(var(--ink-soft))] mt-1">
            Against <span className="font-mono text-[hsl(var(--brass-2))]">{po.po_number}</span> · {po.vendor_snapshot?.name}
          </div>
        </div>
        <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Received on
          <input type="date" data-testid="grn-date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} className="tbb-inp mt-1" />
        </label>
      </div>

      {(() => {
        const total = (po.lines || []).length;
        const hidden = total - lines.length;
        if (hidden <= 0) return null;
        return (
          <div data-testid="grn-hidden-lines-note" className="mt-4 flex items-start gap-2 text-xs p-3 border border-[hsl(var(--brass))]/40 bg-[hsl(var(--brass))]/8 text-[hsl(var(--ink))]">
            <span className="font-semibold text-[hsl(var(--brass-2))] uppercase tracking-wider">Note</span>
            <span>
              This PO has <b>{total}</b> line{total === 1 ? "" : "s"} in total.
              <b> {hidden}</b> {hidden === 1 ? "was" : "were"} already fully received in prior GRN{hidden === 1 ? "" : "s"}.
              Only the <b>{lines.length}</b> line{lines.length === 1 ? "" : "s"} with pending qty {lines.length === 1 ? "is" : "are"} shown below.
            </span>
          </div>
        );
      })()}

      <div className="mt-4 flex items-center justify-between flex-wrap gap-2 p-3 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
        <div className="text-xs text-[hsl(var(--ink-soft))]">
          {lines.length === 0 ? (
            <span data-testid="grn-nothing-pending" className="text-[hsl(6_62%_45%)] font-semibold">All lines already received on this PO — nothing to receive here.</span>
          ) : (
            <><span data-testid="grn-received-count" className="font-semibold text-[hsl(var(--ink))]">{receivedLineCount}</span> of {lines.length} lines have a received qty.</>
          )}
        </div>
        <div className="flex gap-2">
          <button data-testid="grn-receive-all" type="button" onClick={receiveAll} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors">
            <PackageCheck size={12} /> Receive all pending
          </button>
          <button data-testid="grn-clear-all" type="button" onClick={clearAll} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]">
            <RefreshCw size={12} /> Clear
          </button>
        </div>
      </div>

      <div className="mt-6 border border-[hsl(var(--rule))]">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-2))] text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--rule))]">
            <tr>
              <th className="p-2 text-left">#</th>
              <th className="p-2 text-left">Item</th>
              <th className="p-2 text-right w-16">Unit</th>
              <th className="p-2 text-right w-20">Ordered</th>
              <th className="p-2 text-right w-20">Pending</th>
              <th className="p-2 text-right w-36">Received *</th>
              <th className="p-2 text-right w-24">Rate (₹)</th>
              <th className="p-2 text-right w-28">Amount</th>
              <th className="p-2 text-left w-40">Remarks</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--rule))]">
            {lines.map((l, i) => {
              const amt = Number(l.qty_received || 0) * Number(l.rate || 0);
              const over = Number(l.qty_received || 0) - Number(l.qty_pending || 0);
              const overReceipt = over > 1e-6;
              const overNeedsRemark = overReceipt && !(l.remarks || "").trim();
              // CTRL-05 (line level): compare entered rate to the PO's original rate.
              // Thresholds are pulled from procurement Settings (with 5/15 % defaults).
              const poRate = Number(l.po_rate || 0);
              const entered = Number(l.rate || 0);
              const rateDev = poRate > 0 && entered > 0 ? ((entered - poRate) / poRate) * 100 : null;
              const rateDevAbs = rateDev == null ? 0 : Math.abs(rateDev);
              const rateAmber = rateDevAbs > devAmber && rateDevAbs <= devRed;
              const rateRed = rateDevAbs > devRed;
              const rateHighlight = rateAmber || rateRed;
              const rateBorder = rateRed ? "border-[hsl(6_62%_45%)] bg-[hsl(6_60%_98%)]"
                : rateAmber ? "border-[hsl(35_75%_38%)] bg-[hsl(38_90%_96%)]" : "";
              return (
                <tr key={i} data-testid={`grn-line-${i}`}>
                  <td className="p-2 text-[hsl(var(--ink-soft))]">{i + 1}</td>
                  <td className="p-2">{l.item_name}</td>
                  <td className="p-2">{l.unit}</td>
                  <td className="p-2 text-right">{l.qty_ordered}</td>
                  <td className="p-2 text-right text-[hsl(var(--brass-2))]">{l.qty_pending}</td>
                  <td className="p-2">
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" step="0.01" data-testid={`grn-qty-${i}`} value={l.qty_received} onChange={(e) => updateLine(i, { qty_received: e.target.value })} className={`tbb-inp w-full min-w-0 text-sm text-right !px-1.5 ${overReceipt ? "border-[hsl(35_75%_38%)] bg-[hsl(38_90%_96%)]" : ""}`} />
                      <button
                        type="button"
                        data-testid={`grn-fill-full-${i}`}
                        onClick={() => updateLine(i, { qty_received: l.qty_pending })}
                        title={`Fill full pending (${l.qty_pending})`}
                        className="text-[10px] uppercase tracking-wider px-1.5 py-1 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors shrink-0"
                      >Full</button>
                    </div>
                    {overReceipt && (
                      <div data-testid={`grn-over-${i}`} className="mt-1 text-[10px] text-[hsl(35_75%_38%)]">
                        +{over.toLocaleString("en-IN")} over pending{overNeedsRemark ? " · add remark" : ""}
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    <input type="number" min="0" step="0.01" data-testid={`grn-rate-${i}`}
                           value={l.rate} onChange={(e) => updateLine(i, { rate: e.target.value })}
                           className={`tbb-inp w-full text-sm text-right ${rateBorder}`} />
                    {rateHighlight && (
                      <div data-testid={`grn-rate-dev-${i}`}
                           className={`mt-1 flex items-center justify-end gap-1.5 text-[10px] font-semibold ${rateRed ? "text-[hsl(6_62%_45%)]" : "text-[hsl(35_75%_38%)]"}`}>
                        <span>{rateDev > 0 ? "+" : ""}{rateDev.toFixed(1)}% vs PO ₹{poRate.toFixed(2)}</span>
                        <button
                          type="button"
                          data-testid={`grn-use-po-rate-${i}`}
                          onClick={() => updateLine(i, { rate: poRate })}
                          title={`Snap this line back to the authorised PO rate (₹${poRate.toFixed(2)})`}
                          className="inline-flex items-center gap-1 uppercase tracking-wider px-1.5 py-0.5 rounded border border-current hover:bg-current hover:text-[hsl(var(--bg))] transition-colors"
                        >
                          <RefreshCw size={9} /> Use PO
                        </button>
                      </div>
                    )}
                    {rateDev !== null && !rateHighlight && rateDevAbs > 0.5 && (
                      <div className="mt-1 text-[10px] text-[hsl(var(--ink-soft))] text-right">
                        {rateDev > 0 ? "+" : ""}{rateDev.toFixed(1)}% vs PO
                      </div>
                    )}
                  </td>
                  <td className="p-2 text-right">₹{amt.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                  <td className="p-2"><input data-testid={`grn-remarks-${i}`} value={l.remarks} onChange={(e) => updateLine(i, { remarks: e.target.value })} className={`tbb-inp w-full text-sm ${overNeedsRemark ? "border-[hsl(6_62%_45%)]" : ""}`} placeholder={overReceipt ? "why over-receipt?" : "short / damaged / substituted…"} /></td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-[hsl(var(--bg-2))]">
            <tr><td colSpan={7} className="p-2 text-right text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">Subtotal (received value)</td><td className="p-2 text-right">₹{totals.subtotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td><td></td></tr>
            <tr><td colSpan={7} className="p-2 text-right text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">Tax ({totals.tax_percent}%)</td><td className="p-2 text-right">₹{totals.tax_amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td><td></td></tr>
            <tr><td colSpan={7} className="p-2 text-right font-semibold">Total</td><td className="p-2 text-right font-semibold">₹{totals.total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td><td></td></tr>
          </tfoot>
        </table>
      </div>

      <label className="mt-4 block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
        GRN notes
        <textarea data-testid="grn-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="tbb-inp mt-1 w-full resize-none" placeholder="Delivery challan number, discrepancies, etc." />
      </label>

      <div className="mt-6 flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
        <button data-testid="grn-post" disabled={saving} onClick={save} className="btn-brass !py-2.5 !px-5 text-sm">
          {saving ? "Posting..." : "Submit GRN for approval"}
        </button>
      </div>
      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.4rem 0.6rem;font-size:0.85rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}
